import { format, getISOWeek, getISOWeekYear, setISOWeek, setISOWeekYear, startOfISOWeek } from 'date-fns';
import { ForecastChangedProduct, TaskWeeklyPoint } from '@domain/tasks/models/Task';

export const CHART_HEIGHT = 272;

export type IsoWeek = { year: number; week: number };

export interface CompareChartPoint {
  year: number;
  week: number;
  weekKey: string;
  date: string;
  historical: number | null;
  previous: number | null;
  current: number | null;
}

type SeriesRow = IsoWeek & { previous?: number; current?: number };

export const compareIsoWeek = (a: IsoWeek, b: IsoWeek): number =>
  a.year - b.year || a.week - b.week;

export const weekToDate = (year: number, week: number): Date =>
  startOfISOWeek(setISOWeek(setISOWeekYear(new Date(year, 5, 15), year), week));

export const currentIsoWeek = (date = new Date()): IsoWeek => ({
  year: getISOWeekYear(date),
  week: getISOWeek(date),
});

export const todayDateFromChart = (
  chartData: CompareChartPoint[],
  nowWeek: IsoWeek,
): string | undefined =>
  chartData.find((row) => compareIsoWeek(row, nowWeek) === 0)?.date ??
  chartData.find((row) => compareIsoWeek(row, nowWeek) >= 0)?.date ??
  chartData[0]?.date;

export const chartXTicks = (
  chartData: CompareChartPoint[],
  todayDate?: string,
): string[] => {
  if (chartData.length === 0) return [];

  const todayIndex = chartData.findIndex((row) => row.date === todayDate);
  const step = Math.max(2, Math.ceil(chartData.length / 8));
  const anchor = todayIndex >= 0 ? todayIndex : 0;

  return chartData
    .filter((_, index) => Math.abs(index - anchor) % step === 0)
    .map((row) => row.date);
};

export const buildCompareChartData = (
  product: ForecastChangedProduct | null,
  nowWeek: IsoWeek,
): CompareChartPoint[] => {
  if (!product) return [];

  const byKey = new Map<string, SeriesRow>();
  const add = (points: TaskWeeklyPoint[] | undefined, field: 'previous' | 'current') => {
    for (const point of points ?? []) {
      const key = `${point.year}-${point.week}`;
      const existing = byKey.get(key) ?? { year: point.year, week: point.week };
      existing[field] = point.quantity;
      byKey.set(key, existing);
    }
  };

  add(product.previousWeekly, 'previous');
  add(product.currentWeekly, 'current');

  return Array.from(byKey.values())
    .sort(compareIsoWeek)
    .map((row) => {
      const vsNow = compareIsoWeek(row, nowWeek);
      const sales = row.current ?? row.previous;

      return {
        year: row.year,
        week: row.week,
        weekKey: `${row.year}-${row.week}`,
        date: format(weekToDate(row.year, row.week), 'yyyy-MM-dd'),
        historical: vsNow <= 0 ? sales ?? null : null,
        previous: vsNow >= 0 ? row.previous ?? null : null,
        current: vsNow >= 0 ? row.current ?? null : null,
      };
    });
};
